import assert from '@/sui/tools/assert'
import notesImpl from '@/sui/data/sheets/edit/notes/common'

const strToDateCache = {}

function strToDate (str) {
  const cache = strToDateCache[str]
  if (cache != null) {
    return cache
  }

  const [day, month, year] = str.split('/')
  const result = new Date(year, month - 1, day)
  strToDateCache[str] = result
  return result
}

function getDate (sheet, field) {
  const str = sheet[field]
  if (str == null || str === '') {
    return null
  }

  return strToDate(sheet[field])
}

function compareValues (first, second, prior) {
  if (prior) {
    return first < second
  }

  return first > second
}

function setDateError (first, second, inferior) {
  if (second.note != null && second.note.type !== 'error') {
    const comparison = inferior ? 'inférieure ou égale' : 'supérieure ou égale'
    second.note.type = 'error'
    second.note.tooltip = `
La ${second.text} doit être ${comparison} à la ${first.text}.`
  }
}

function setNumericError (first, second, inferior) {
  if (second.note != null && second.note.type !== 'error') {
    const comparison = inferior ? 'inférieur ou égal' : 'supérieur ou égal'
    second.note.type = 'error'
    second.note.tooltip = `
Le ${second.text} doit être ${comparison} au ${first.text}.`
  }
}

function compareDates (comparisons) {
  comparisons.forEach(
    c => {
      const p = c.s.note == null
      compareFields(
        p ? c.s : c.f, p ? c.f : c.s, p, (f) => f.date, setDateError
      )
    }
  )
}

function compareNumerics (first, second, prior) {
  compareFields(
    first, second, prior, (f) => parseInt(f.value, 10), setNumericError
  )
}

function compareFields (first, second, prior, getField, setError) {
  const inferior = prior === true
  assert.notNull(
    second.note,
    `Invalid note for «${second.text}» (${first.text})`
  )
  const firstValue = getField(first)
  const secondValue = getField(second)
  if (firstValue != null && secondValue != null &&
      compareValues(firstValue, secondValue, inferior)) {
    setError(first, second, inferior)
    setError(second, first, !inferior)
  }
}

function checkNumerics (result, sheet, list) {
  list.forEach(
    c => {
      const v = sheet[c.field]
      if (v != null) {
        const note = result[c.field]
        const numeric = parseInt(v, 10)
        if (c.bounds.special == null ||
            c.bounds.special.length === 0 ||
            c.bounds.special.indexOf(numeric) === -1) {
          if (c.bounds.lower != null && numeric < c.bounds.lower) {
            note.type = c.message.belowType != null ? c.message.belowType : c.message.type
            note.tooltip = c.message.below
          } else if (c.bounds.upper != null && numeric > c.bounds.upper) {
            note.type = c.message.aboveType != null ? c.message.aboveType : c.message.type
            note.tooltip = c.message.above
          }
        }
      }
    }
  )
}

function dateField (notes, sheet, cfg) {
  assert.notNull(notes)
  assert.notNull(sheet)
  assert.notNull(cfg)
  assert.notNull(cfg.config)

  const field = cfg.field
  assert.notNull(field)

  const text = cfg.config.text
  assert.notNull(text)

  const note = notes[field]
  assert.notNull(note)

  const result = {
    date: getDate(sheet, field),
    text: `date ${text}`,
    note: note
  }
  return result
}

function collectDates (notes, sheet, fields) {
  assert.notNull(notes)
  assert.notNull(sheet)
  assert.isArray(fields)

  const result = {}
  for (let i = 0; i !== fields.length; ++i) {
    const item = fields[i]
    assert.notNull(item, `Item ${i} should not be null`)
    result[item.field] = dateField(notes, sheet, item)
  }
  return result
}

function isEmptyPickListField (sheet, step) {
  if (step.type !== 'pickList') {
    return false
  }

  return !notesImpl.checkPickList.atLeastOne(sheet, [step.field])
}

function buildNotes (reference, sheet, surveillance) {
  const result = {}

  const stack = [{ steps: reference.fields, target: sheet, note: result }]
  while (stack.length > 0) {
    const it = stack[0]
    for (let i in it.steps) {
      const step = it.steps[i]
      if (step.type !== 'array') {
        const note = { referenceField: step.config }
        if (!step.config.optional && !step.pop &&
            (step.ignore == null || !step.ignore(it.target, sheet)) &&
            (it.target[step.field] == null || isEmptyPickListField(sheet, step))) {
          note.type = 'error'
          note.tooltip = `Champ requis pour la validation de la fiche`
          note.missing = true
        }
        it.note[step.field] = note
      } else {
        it.note[step.field] = []
        const items = it.target[step.field]
        for (let j in items) {
          const subnote = { referenceField: step.config }
          it.note[step.field].push(subnote)
          stack.push({
            steps: step.config.fields,
            note: subnote,
            target: items[j]
          })
        }
      }
    }

    stack.shift()
  }

  return reference.buildNotes(result, sheet, surveillance)
}

const oldest = {
  date: new Date(1900, 0, 1),
  text: 'date minimale (01/01/1900)'
}

function newest (year) {
  const y = year
  return {
    date: new Date(y, 11, 31),
    text: `date maximale (31/12/${y})`
  }
}

function start (year) {
  return {
    date: new Date(year, 0, 1),
    text: `de début de la surveillance (01/01/${year})`
  }
}

function neoOldest (year) {
  return {
    date: new Date(year - 1, 5, 30),
    text: `date minimale (01/07/${year - 1})`
  }
}

function now () {
  return {
    date: new Date(),
    text: `date d'aujourd'hui`
  }
}

export default {
  buildNotes: buildNotes,
  collectDates: collectDates,
  dateField: dateField,
  compareDates: compareDates,
  compareNumerics: compareNumerics,
  checkNumerics: checkNumerics,
  getDate: getDate,
  oldest: {
    '2019': oldest,
    '2020': oldest,
    '2021': oldest,
    '2022': oldest,
    '2023': oldest,
    '2024': oldest,
    '2025': oldest,
    '2026': oldest
  },
  newest: {
    '2019': newest(2019),
    '2020': newest(2020),
    '2021': newest(2021),
    '2022': newest(2022),
    '2023': newest(2023),
    '2024': newest(2024),
    '2025': newest(2025),
    '2026': newest(2026)
  },
  start: {
    '2019': start(2019),
    '2020': {
      date: new Date(2019, 10, 1),
      text: `de début de la surveillance (01/11/2019)`
    },
    '2021': start(2021),
    '2022': start(2022),
    '2023': start(2023),
    '2024': start(2024),
    '2025': start(2025),
    '2026': start(2026)
  },
  neoOldest: {
    '2019': neoOldest(2019),
    '2020': neoOldest(2020),
    '2021': neoOldest(2021),
    '2022': neoOldest(2022),
    '2023': neoOldest(2023),
    '2024': neoOldest(2024),
    '2025': neoOldest(2025),
    '2026': neoOldest(2026)
  },
  now
}
